import { useEffect, useRef } from 'react';
import maplibregl, { type Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { BoardRoute } from '../lib/types';
import {
  STALLED_MESSAGE,
  emptyCollection,
  setData,
  useMapInstance,
} from './map/mapbase';
import { clearLabels, syncLabels } from './map/labels';
import { stopLabel } from '../lib/format';
import { useRoadPaths, useRoadShapes } from '../lib/roads';

/**
 * One route, drawn on its own.
 *
 * The drawer used to be a list of times with no picture, which answers when a
 * technician gets somewhere but never where that is. A dispatcher reading a
 * stop wants both, and switching to the board and hunting for the same route
 * loses the place they were reading.
 *
 * Deliberately not the dispatch map with everything filtered out: this one
 * frames a single route, numbers its stops in visit order, and re-frames when a
 * different stop is opened, none of which the board should do.
 */
export function RouteMiniMap({
  route,
  highlightStopId,
  territoryId,
  date,
  planVersion,
}: {
  route: BoardRoute;
  highlightStopId: string | null;
  /**
   * Which day's road shapes to ask for. Optional, so a caller that has no
   * board to hand still gets a map -- it just draws straight lines and the
   * switch says why.
   */
  territoryId?: string | null;
  date?: string;
  planVersion?: number | null;
}) {
  const labelsRef = useRef<Map<string, Marker>>(new Map());
  const fittedRouteRef = useRef<string | null>(null);
  /* Where the day starts and ends; one marker, moved rather than rebuilt. */
  const homeRef = useRef<maplibregl.Marker | null>(null);

  /*
   * The same switch the board uses, not a second one.
   *
   * On one route zoomed into a few streets, the difference between a straight
   * line and the road actually driven is the whole question -- two stops either
   * side of a river read as neighbours until the line goes round by the bridge.
   */
  const [showRoads, setShowRoads] = useRoadShapes();
  const { roads, loading } = useRoadPaths(
    territoryId ?? null,
    date ?? '',
    showRoads && Boolean(territoryId && date),
    planVersion ?? null,
  );
  const unavailable = territoryId && date ? roads !== null && !roads.available : true;

  const { containerRef, mapRef, ready, stalled } = useMapInstance((map) => {
    map.addSource('line', { type: 'geojson', data: emptyCollection() as never });
    map.addSource('points', { type: 'geojson', data: emptyCollection() as never });

    map.addLayer({
      id: 'line',
      type: 'line',
      source: 'line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#0f6fbf', 'line-width': 2.5, 'line-opacity': 0.75 },
    });

    map.addLayer({
      id: 'points',
      type: 'circle',
      source: 'points',
      paint: {
        'circle-radius': ['case', ['get', 'highlighted'], 8, 5],
        'circle-color': [
          'case',
          ['==', ['get', 'status'], 'completed'], '#1c7f4d',
          ['==', ['get', 'status'], 'failed'], '#b93a2b',
          ['get', 'highlighted'], '#b4531a',
          '#0f6fbf',
        ],
        'circle-stroke-width': ['case', ['get', 'highlighted'], 3, 1.4],
        'circle-stroke-color': '#ffffff',
      },
    });
  });

  useEffect(() => {
    const markers = labelsRef.current;
    return () => clearLabels(markers);
  }, []);

  /**
   * Where the driver sets off from, and comes back to.
   *
   * The line already ran depot -> stops -> depot, but nothing said what the
   * two ends were: the route appeared to start from a blank patch of map and
   * return to it. On this operator's data the depot is the technician's own
   * home address, which is exactly the thing a dispatcher reads a single
   * route to understand -- how far the first and last jobs are from where the
   * van actually sleeps.
   *
   * A DOM marker rather than a symbol layer, for the reason given in
   * map/labels: a symbol layer needs glyphs and a sprite, and this has to keep
   * working on the blank canvas we fall back to when no basemap loads.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const depot = route.startDepot;
    if (!depot) {
      /* A vehicle with no depot set has nothing to draw, and must not break. */
      homeRef.current?.remove();
      homeRef.current = null;
      return;
    }

    if (!homeRef.current) {
      const element = document.createElement('div');
      element.className = 'map-home';
      element.innerHTML =
        `<span class="map-home-pin" aria-hidden="true"></span>` +
        `<span class="map-home-name"></span>`;
      homeRef.current = new maplibregl.Marker({ element, anchor: 'bottom' })
        .setLngLat([depot.lng, depot.lat])
        .addTo(map);
    } else {
      homeRef.current.setLngLat([depot.lng, depot.lat]);
    }

    const element = homeRef.current.getElement();
    element.title = `${depot.name} — starts and ends here`;
    const name = element.querySelector('.map-home-name');
    if (name) name.textContent = depot.name;
  }, [route.startDepot, ready, mapRef]);

  /*
   * Teardown reads the ref, it does not capture it.
   *
   * `const marker = homeRef.current` here captures null -- the marker does not
   * exist until the effect above runs. Under StrictMode that left the first
   * mount's marker in the ref after its map was destroyed, so the second pass
   * found it non-null, took the move branch, and never called addTo on the live
   * map: no home pin at all, and no error. Nulling the ref forces a rebuild
   * against whichever map is current.
   */
  useEffect(() => {
    return () => {
      homeRef.current?.remove();
      homeRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const located = route.stops.filter((s) => s.lat !== null && s.lng !== null);

    /*
     * Numbers rather than names here. On one route the question is the order of
     * the visits, and eight order references stacked over a metro is unreadable.
     */
    syncLabels(
      map,
      labelsRef.current,
      located.map((stop) => ({
        key: stop.id,
        /* `4.1`, the same label the timeline and the driver app use. */
        text: stopLabel(stop, route.routeNumber ?? route.vehicleIdx),
        lat: stop.lat as number,
        lng: stop.lng as number,
        tone: stop.id === highlightStopId ? 'late' : undefined,
      })),
    );

    if (!ready) return;

    /*
     * The straight line is always built: it frames the map, and it is the
     * fallback when there is no road shape for this route. The road shape only
     * replaces what is drawn, never what the viewport is fitted to -- a route
     * and its road follow the same stops, so the framing is the same either
     * way and recomputing it would make the map jump on every toggle.
     */
    const coords: [number, number][] = [];
    if (route.startDepot) coords.push([route.startDepot.lng, route.startDepot.lat]);
    for (const stop of located) coords.push([stop.lng as number, stop.lat as number]);
    if (route.startDepot) coords.push([route.startDepot.lng, route.startDepot.lat]);

    const road = route.id ? roads?.paths[route.id] : undefined;
    const drawn = road && road.length > 1 ? road : coords;

    setData(map, 'line', {
      type: 'FeatureCollection',
      features: drawn.length > 1
        ? [{ type: 'Feature', geometry: { type: 'LineString', coordinates: drawn }, properties: {} }]
        : [],
    });

    setData(map, 'points', {
      type: 'FeatureCollection',
      features: located.map((stop) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [stop.lng as number, stop.lat as number] },
        properties: {
          status: stop.status,
          highlighted: stop.id === highlightStopId,
        },
      })),
    });

    /*
     * Frame the whole route when it first opens, then leave the viewport alone
     * so a dispatcher scrolling the list is not yanked around -- except to bring
     * a newly opened stop into view.
     */
    if (fittedRouteRef.current !== route.id && coords.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      for (const c of coords) bounds.extend(c);
      map.fitBounds(bounds, { padding: 40, duration: 0 });
      fittedRouteRef.current = route.id;
    } else if (highlightStopId) {
      const stop = located.find((s) => s.id === highlightStopId);
      if (stop && !map.getBounds().contains([stop.lng as number, stop.lat as number])) {
        map.easeTo({ center: [stop.lng as number, stop.lat as number], duration: 400 });
      }
    }
  }, [route, highlightStopId, ready, mapRef, roads]);

  return (
    <div className="drawer-map">
      <div className="map" ref={containerRef} />
      {stalled ? (
        <div className="map-stalled" role="status">
          <b>The basemap did not load</b>
          <p>The stops below are unaffected. {STALLED_MESSAGE}</p>
        </div>
      ) : null}

      {/*
        * One switch, so it is a button rather than the board's menu: there is
        * nothing else to put beside it here, and a menu holding a single item
        * is a click in front of a click.
        */}
      <button
        type="button"
        className="map-control map-control-mini"
        /* The effective state, not the stored one: a switch that reads as on
         * while nothing follows a road is worse than one that reads as off. */
        aria-pressed={showRoads && !unavailable}
        disabled={unavailable}
        onClick={() => setShowRoads(!showRoads)}
        title={
          unavailable
            ? (roads?.reason ??
              'No routing engine is configured, so only straight lines are available')
            : showRoads
              ? 'Draw this route as straight lines between stops'
              : 'Draw this route along the roads actually driven'
        }
      >
        {loading ? (
          <span className="spinner" />
        ) : (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M3 14c0-4 4-3.5 4-6S3.5 5 3.5 2M12.5 2c0 4-4 3.5-4 6s4 2 4 6"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        )}
        Follow the roads
      </button>
    </div>
  );
}
